import {Component, OnDestroy, OnInit} from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators
} from "@angular/forms";
import * as Validation from "../../../shared/constants/validation.constants";
import {CommonService} from "../../../shared/services/common.service";
import {debounceTime, Subject, takeUntil} from "rxjs";
import {
  roleOptionsConfiguration,
  roles,
  rolesLabelMap,
  sortOrderMap
} from "../../../shared/constants/common.constants";
import {Store} from "@ngrx/store";
import * as fromRoot from "../../../../state/app.state";
import {
  autogeneratedUsername,
  profile,
  verifyUsername
} from "../../../shared/core/shared.selectors";
import {
  clientFilter,
  createUserDataInfo,
  emailPreviewDataInfo,
  groupFilter,
  inviteUser
} from "../core/invite.selectors";
import {
  ClearAdultUsernameFilter,
  ClearClientFilter,
  ClearParticipantUsernameFilter,
  FetchClientFilterRequest,
  FetchEmailPreviewDataRequest,
  FetchGroupsFilterRequest,
  InviteUserRequest,
  ReleaseUsernameRequest,
  SetEmailPreviewData
} from "../core/invite.actions";
import {DropdownOption} from "../../../../models/dropdown-option.model";
import {Invite} from "../services/invite";
import {v4 as uuidv4} from "uuid";
import {ClearAutoGeneratedUsername} from "../../../shared/core/shared.actions";
import {Filter} from "../../../../models/filter";
import * as moment from "moment/moment";
import {ActivatedRoute} from "@angular/router";
import * as $ from "jquery";
import {
  EmailPreviewComponent
} from "../components/email-preview/email-preview.component";

@Component({
  selector: "app-invite-user",
  templateUrl: "./invite-user.component.html",
  styleUrls: ["./invite-user.component.css"]
})
export class InviteUserComponent implements OnInit, OnDestroy {
  isExpanded: boolean;
  isAdultExpanded: boolean = true;
  orgSelected: boolean = false;
  displayToggleButton: boolean;
  selectedRole: number;
  userRoleName: string;
  userRole: number;
  removeSpace: boolean;
  selectedClient: any;
  rolesOptions: DropdownOption[] = [];
  groupOptions: Filter = {
    list: [],
    total: null,
    pageNumber: null,
    limit: -1,
    hasNext: false,
    hasPrev: false,
    loading: false
  };
  clientOptions: Filter = {
    list: [],
    total: null,
    pageNumber: null,
    limit: -1,
    hasNext: false,
    hasPrev: false,
    loading: false
  };
  errorMessages: any = {};
  validation = Validation;
  configuration: any[] = [];
  participantConfiguration: any[] = [];
  clientId: any;
  groupId: any;
  loading: boolean = false;
  previewLoading: boolean = false;
  payloadRoleKeyLabelMap = {
    1: "superAdmins",
    2: "clientAdmins",
    3: "coaches",
    4: "adults",
  };
  maxDobDate: any;
  minDobDate: any;
  errorMessageMap = {
    role: {
      required: "This field is required."
    },
    client: {
      required: "This field is required."
    },
    group: {
      required: "This field is required."
    }
  };
  form = new FormGroup({
    role: new FormControl<number>({
      value: 0,
      disabled: false
    }, [Validators.required]),
    client: new FormControl<number>({value: 0, disabled: false}),
    searchClient: new FormControl({value: "", disabled: false}),
    group: new FormControl<number>({value: 0, disabled: false}),
    searchGroup: new FormControl({value: "", disabled: false}),
  });
  newUserFormErrorMessageMap = {
    firstName: {
      required: "This field is required.",
      pattern: "Only alphabets allowed",
      minlength: "Minimum 1 characters required",
      maxlength: "Maximum 50 characters accepted",
    },
    lastName: {
      required: "This field is required.",
      pattern: "Only alphabets allowed",
      minlength: "Minimum 1 characters required",
      maxlength: "Maximum 50 characters accepted"
    },
    emailId: {
      required: "This field is required.",
      pattern: "Invalid Email Id"
    },
    username: {
      required: "This field is required.",
      minlength: "Minimum 2 characters required",
      spaceCheckFailed: "Spaces not allowed",
      specialCharCheckFailed: "Only [._-] symbols allowed",
      digitsAtStartCheckFailed: "Username cannot start with digits & [._-]"
    },
    dob: {
      required: "This field is required.",
      invalidParticipantPastDate: "Participant age should not be more than 18 years",
      invalidParticipantFutureDate: "Today's and future date is not allowed"
    },
  };
  existingUserFormErrorMessageMap = {
    emailId: {
      required: "This field is required.",
      pattern: "Invalid Email Id"
    },
    userId: {
      required: "This field is required."
    },
  };
  isVerifyDisabled: boolean = false;
  useSystemMailClient: boolean;
  previousClientIdValue: number;
  skipClientIdChangeListener = false;
  isAutogenerateUsernameLoading: boolean = false;
  isVerifyUsernameLoading: boolean = false;
  userCreated: boolean = false;
  emailPreviewData: any;
  protected readonly rolesLabelMap = rolesLabelMap;
  protected readonly roles = roles;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private commonService: CommonService,
    private inviteService: Invite,
    private store: Store<fromRoot.State>,
    private fb: FormBuilder,
    private invite: Invite,
    private route: ActivatedRoute,
  ) {
    this.addNewConfiguration();
    this.addParticipantConfiguration();
  }

  ngOnInit() {
    this.store.dispatch(ClearClientFilter());

    this.maxDobDate = moment().subtract(1, "days");
    this.minDobDate = moment().subtract(18, "year");

    this.invite.isExpandAdultSection.pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.isAdultExpanded = data;
      });

    this.store.select(profile).pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data?.role > roles.superAdmin) {
          this.clientId = data?.clientId;
          if (this.clientId) {
            this.orgSelected = true;
          }
        }
        this.useSystemMailClient = data?.useSystemMailClient;
        this.userRole = data?.role;
        this.userRoleName = rolesLabelMap[this.userRole];
        this.rolesOptions = roleOptionsConfiguration[this.userRole];
      });

    this.form.controls["role"].valueChanges.pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        // Making OrgSelected False at the start
        this.orgSelected = false;
        this.selectedRole = Number(data);
        this.invite.resetForm.next();
        this.form.controls["client"].reset();
        this.form.controls["group"].reset();
        if (
          this.userRole === roles["superAdmin"] &&
          this.selectedRole === roles["superAdmin"]
        ) {
          this.orgSelected = true;
        }

        if (
          this.userRole === roles["superAdmin"] &&
          this.selectedRole !== roles["superAdmin"]
        ) {
          this.fetchClientsFilterList();
        }

        if (
          this.userRole === roles["superAdmin"] &&
          [roles["adult"], roles["participant"]].includes(this.selectedRole)
        ) {
          this.skipClientIdChangeListener = true;
          this.groupOptions.list = [];
          this.form.controls["group"].disable();
          this.form.controls["group"].reset();
        }

        if (
          (
            this.userRole === roles["clientAdmin"] ||
            this.userRole === roles["coach"]
          )
          && (this.selectedRole === roles["adult"] ||
              this.selectedRole ===
              roles["participant"])
        ) {
          this.skipClientIdChangeListener = true;
          this.store.dispatch(FetchGroupsFilterRequest({
            payload: {
              status: [
                1, 2
              ],
              sortColumn: 'startDate',
              sortOrder: sortOrderMap.desc
            }
          }));
        }

        if (
          (this.userRole === roles["clientAdmin"] ||
           this.userRole === roles["coach"]) && (this.clientId)
        ) {
          this.orgSelected = true
        }

        this.removeSpace = (this.userRole === roles["clientAdmin"] &&
                            this.selectedRole === roles["adult"]) ||
                           (this.userRole === roles["coach"] &&
                            this.selectedRole === roles["adult"]);

        this.isExpanded = this.selectedRole === roles["superAdmin"] ||
                          this.selectedRole === roles["clientAdmin"] ||
                          this.selectedRole === roles["coach"];

        this.displayToggleButton = !(
          this.selectedRole === roles["superAdmin"] ||
          this.selectedRole === roles["clientAdmin"] ||
          this.selectedRole === roles["coach"]
        );

        this.configuration = [];
        this.addNewConfiguration();
      });

    this.store.select(clientFilter).pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        const list = data?.list?.filter((item) => item.key !== this.selectedClient?.key);
        this.clientOptions.list.push(...(list || []));
        this.clientOptions.loading = data?.loading;
        this.clientOptions.hasNext = data?.hasNext;
        // this.clientOptions.hasPrev = data?.hasPrev;
        this.clientOptions.pageNumber = data?.pageNumber;
        this.clientOptions.limit = data?.limit;
      });

    this.store.select(groupFilter).pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          // Format group names to include date when Participant/Adult is selected
          const shouldFormat = this.selectedRole === roles["adult"] || this.selectedRole === roles["participant"];
          const formattedList = shouldFormat && data.list && data.list.length > 0
            ? data.list.map(item => ({
                ...item,
                value: item.startDate 
                  ? `${item.value} (${this.formatDate(item.startDate)})`
                  : item.value
              }))
            : (data?.list || []);
          
          this.groupOptions.list = formattedList;
          this.groupOptions.loading = data?.loading || false;
          this.groupOptions.hasNext = data?.hasNext || false;
          this.groupOptions.pageNumber = data?.pageNumber || null;
        }
      });

    this.invite.resetForm.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.store.dispatch(ClearAutoGeneratedUsername());
      this.store.dispatch(ClearParticipantUsernameFilter());
      this.store.dispatch(ClearAdultUsernameFilter());
      this.configuration = [];
      this.participantConfiguration = [];
      this.addNewConfiguration();
      this.addParticipantConfiguration();
      this.form.controls["client"].reset();
      this.form.controls["group"].reset();
    });

    const clientId = this.route.snapshot.queryParams["clientId"];
    if (clientId) {
      this.clientId = clientId;
      const selectedRole = this.rolesOptions.find((item) => item.key == roles["clientAdmin"]);
      this.form.patchValue({
        role: +selectedRole.key,
        client: +clientId
      }, {emitEvent: true});
    }

    this.form.controls.role.valueChanges.pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.commonService.updateQueryParams({
          "clientId": null
        });
        if (this.userRole === roles["superAdmin"]) {
          if (data !== roles.superAdmin) {
            this.form.controls.client.addValidators([Validators.required]);
          } else {
            this.form.controls.client.clearValidators();
            this.form.controls.client.setErrors(null);
          }
        } else {
          this.form.controls.client.clearValidators();
          this.form.controls.client.setErrors(null);
        }
      });

    this.form.controls.client.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        // Handle clientId update for super admin selecting non-super admin roles
        if (
          this.userRole === roles["superAdmin"] &&
          this.selectedRole !== roles["superAdmin"] &&
          data
        ) {
          this.clientId = data;
          if (this.clientId) {
            this.orgSelected = true;
          }
        }

        // Handle group fetching for super admin selecting adult/participant roles
        if (
          this.userRole === roles["superAdmin"] &&
          [roles["adult"], roles["participant"]].includes(this.selectedRole)
        ) {
          this.form.controls["group"].reset();
          this.groupOptions.list = [];
          if (data) {
            this.orgSelected = true;
            this.form.controls["group"].enable();
            this.store.dispatch(FetchGroupsFilterRequest({
              payload: {
                clientId: data,
                status: [1, 2],
                sortColumn: 'startDate',
                sortOrder: sortOrderMap.desc
              }
            }));
          } else {
            this.form.controls["group"].disable();
          }
        }

        // Handle confirmation dialog for adult/participant role changes
        if (this.skipClientIdChangeListener || !this.form.controls['client'].dirty || !this.previousClientIdValue) {
          this.previousClientIdValue = data
          this.skipClientIdChangeListener = ![
            roles.adult, roles.participant
          ].includes(this.selectedRole);
          return;
        }

        this.commonService.openConfirmationDialog({
          data: {
            message: 'Changing the Organization will reset all the existing' +
                     ' users data if entered any!',
            submitText: 'Proceed',
            handleSubmit: () => {
              this.clientId = data;
              this.previousClientIdValue = data;
              this.inviteService.resetExistingUserForm.next(true);
              this.toggleButtonClicked(true, this.participantConfiguration[0], true)
              this.configuration = [];
              this.participantConfiguration = [];
              this.addNewConfiguration();
              this.addParticipantConfiguration();
              if (!this.form?.value?.client) {
                this.orgSelected = false;
              }
              this.commonService.closeDialog();
            },
            cancelText: 'Cancel',
            handleCancel: () => {
              this.commonService.closeDialog();
              this.clientId = this.previousClientIdValue;
              this.form.patchValue({
                client: this.clientId
              }, {emitEvent: false});
            }
          }
        })

      });

    this.form.controls["group"].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.groupId = data;
      });


    this.form.controls.searchClient.valueChanges.pipe(debounceTime(1000), takeUntil(this.onDestroy))
      .subscribe(data => {
        this.clientOptions.pageNumber = 1;
        this.clientOptions.list = [];
        if (this.selectedClient) {
          this.clientOptions.list.push(this.selectedClient);
        }
        this.fetchClientsFilterList();
      });

    this.form.controls['searchGroup'].valueChanges.pipe(takeUntil(this.onDestroy), debounceTime(1000))
      .subscribe(value => {
        this.fetchListGroups(1, value)
      })

    this.store.select(autogeneratedUsername).pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.isAutogenerateUsernameLoading = data?.loading
        let isUsernameVerified = false;

        if (!data?.loading && data?.username) {
          isUsernameVerified = true;
        }

        this.setUsernameVerificationStatus({
          uuid: data?.uuid,
          isUsernameVerified
        });
      });

    this.store.select(verifyUsername).pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        let isUsernameVerified = false;

        if (!data?.loading && data?.isAvailable) {
          isUsernameVerified = true;
        }

        this.setUsernameVerificationStatus({
          uuid: data?.uuid,
          isUsernameVerified
        });
      });

    this.invite.markUsernameAsUnverified.pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.setUsernameVerificationStatus({
          uuid: data?.uuid,
          isUsernameVerified: false
        });
      });

    this.invite.markUsernameAsVerified.pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.setUsernameVerificationStatus({
          uuid: data?.uuid,
          isUsernameVerified: true
        });
      });

    this.store.select(inviteUser).pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.loading = data?.loading;
      });

    this.inviteService.isVerifyDisabled.pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.isVerifyDisabled = data;
      });

    this.commonService.onNavigationEnd.pipe(takeUntil(this.onDestroy))
      .subscribe(d => {
        const urlParams = {
          params: this.route.snapshot.params,
          queryParams: this.route.snapshot.queryParams
        };

        const prevUrlParams = this.commonService.urlParams.getValue();

        if (d.isRouteChanged || JSON.stringify(prevUrlParams) !== JSON.stringify(urlParams)) {
         this.commonService.urlParams.next(urlParams);
        }
      });

    this.store.select(emailPreviewDataInfo)
      .subscribe(data => {
        this.previewLoading = data?.loading
      });

    this.store.select(verifyUsername)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.isVerifyUsernameLoading = data?.loading
      })

    this.store.select(createUserDataInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data && data?.emailPreviewData.length) {
          this.userCreated = true
          this.emailPreviewData = data?.emailPreviewData;
          this.inviteService.disableForm.next(true);
        }
      });

  }

  fetchClientsFilterList() {
    const payload: any = {
      pageNumber: this.clientOptions.pageNumber,
      limit: this.clientOptions.limit,
      status: [2, 3]
    };
    if (this.form.controls.searchClient.value.trim()) {
      payload.search = this.form.controls.searchClient.value;
    }

    if (this.form.controls['client'].value) {
      payload.shouldInclude = this.form.controls['client'].value
    }

    this.store.dispatch(FetchClientFilterRequest({payload}));
  }

  clientsOnNextPage() {
    this.clientOptions.pageNumber = this.clientOptions.pageNumber + 1;
    this.fetchClientsFilterList();
  }

  clientsOnPrevPage() {
    this.clientOptions.pageNumber = this.clientOptions.pageNumber - 1;
    this.fetchClientsFilterList();
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField))
    };
  }

  addParticipantConfiguration() {
    this.participantConfiguration = [
      ...this.participantConfiguration,
      {
        uuid: uuidv4(),
        isUserVerified: true,
        isNewUser: true,
        isNewParticipant: true,
        newUserFormErrorMessages: {},
        existingUserFormErrorMessages: {},
        newUserFormGroup: this.fb.group({
          firstName: new FormControl({value: "", disabled: false}, {
            validators: [
              Validators.required,
              Validators.pattern(this.validation.user.firstName.regex),
              Validators.minLength(this.validation.user.firstName.length.min),
              Validators.maxLength(this.validation.user.firstName.length.max),
            ],
          }),
          lastName: new FormControl({value: "", disabled: false}, {
            validators: [
              Validators.required,
              Validators.pattern(this.validation.user.lastName.regex),
              Validators.minLength(this.validation.user.lastName.length.min),
              Validators.maxLength(this.validation.user.lastName.length.max),
            ],
          }),
          emailId: new FormControl({value: "", disabled: false}, {
            validators: [
              Validators.required,
              Validators.pattern(this.validation.email.regex)
            ]
          }),
          username: new FormControl({value: "", disabled: true}, {
            validators: [
              Validators.required,
              this.commonService.validateUsername,
              Validators.minLength(this.validation.user.username.length.min)
            ]
          }),
          dob: new FormControl({value: "", disabled: false}, {
            validators: [
              Validators.required,
              this.validateDateOfBirth.bind(this)
            ]
          }),
        }),
        existingUserFormGroup: new FormGroup({
          emailId: new FormControl({value: "", disabled: false}, {
            validators: [
              Validators.required,
              Validators.pattern(this.validation.email.regex)
            ]
          }),
          userId: new FormControl({value: "", disabled: false}, {
            validators: [
              Validators.required
            ]
          }),
          searchUsername: new FormControl({value: "", disabled: false}),
        })
      }
    ];
  }

  addNewConfiguration() {
    if (this.emailPreviewData) {
      // skip when email preview
      return;
    }

    this.configuration = [
      ...this.configuration,
      {
        uuid: uuidv4(),
        isUserVerified: true,
        isNewUser: true,
        isNewParticipant: true,
        newUserFormErrorMessages: {},
        existingUserFormErrorMessages: {},
        newUserFormGroup: this.fb.group({
          firstName: new FormControl({value: "", disabled: false}, {
            validators: [
              Validators.required,
              Validators.pattern(this.validation.user.firstName.regex),
              Validators.minLength(this.validation.user.firstName.length.min),
              Validators.maxLength(this.validation.user.firstName.length.max),
            ],
          }),
          lastName: new FormControl({value: "", disabled: false}, {
            validators: [
              Validators.required,
              Validators.pattern(this.validation.user.lastName.regex),
              Validators.minLength(this.validation.user.lastName.length.min),
              Validators.maxLength(this.validation.user.lastName.length.max),
            ],
          }),
          emailId: new FormControl({value: "", disabled: false}, {
            validators: [
              Validators.required,
              Validators.pattern(this.validation.email.regex)
            ]
          }),
          username: new FormControl({value: "", disabled: true}, {
            validators: [
              Validators.required,
              this.commonService.validateUsername,
              Validators.minLength(this.validation.user.username.length.min)
            ]
          }),
        }),
        existingUserFormGroup: new FormGroup({
          emailId: new FormControl({value: "", disabled: false}, {
            validators: [
              Validators.required,
              Validators.pattern(this.validation.email.regex)
            ]
          }),
          userId: new FormControl({value: "", disabled: false}, {
            validators: [
              Validators.required
            ]
          }),
          searchUsername: new FormControl({value: "", disabled: false}),
          isLar: new FormControl({value: false, disabled: false})
        })
      },
    ];
  }

  toggleButtonClicked(event, obj, isParticipant?) {
    obj.isNewUser = event;
    obj.isNewParticipant = event;

    if (isParticipant) {
      this.configuration.forEach((eachUser) => {
        eachUser.newUserFormGroup.reset();
        eachUser.existingUserFormGroup.reset();
      })
      this.participantConfiguration.forEach((eachParticipant) => {
        if (obj.uuid === eachParticipant.uuid) {
          eachParticipant.newUserFormGroup.reset();
          eachParticipant.existingUserFormGroup.reset();
          eachParticipant.newUserFormErrorMessages = {};
          this.store.dispatch(ClearParticipantUsernameFilter());
        }
      });
    } else {
      this.configuration.forEach((eachUser) => {
        if (obj.uuid === eachUser.uuid) {
          eachUser.newUserFormGroup.reset();
          eachUser.existingUserFormGroup.reset();
          eachUser.newUserFormErrorMessages = {};
          this.store.dispatch(ClearAdultUsernameFilter());
        }
      });
    }

    if (isParticipant) {
      this.isAdultExpanded = obj.isNewParticipant;
    }
  }

  submit(type) {
    let hasError = false;
    if (this.form.invalid) {
      this.checkForErrors();
      setTimeout(() => {
        $(".form-field.ng-invalid")[0].parentElement.scrollIntoView();
      }, 0);
      hasError = true;
    }

    const userDetails = [];
    let participant: any;

    this.configuration.forEach(eachConfig => {
      if (eachConfig["isNewUser"]) {
        eachConfig["newUserFormErrorMessages"] = {
          ...(this.commonService.checkFormValidation(eachConfig["newUserFormGroup"], this.newUserFormErrorMessageMap))
        };
        if (eachConfig["newUserFormGroup"].controls["username"].value &&
            !eachConfig.isUserVerified && !eachConfig["newUserFormGroup"].controls["username"].invalid) {
          eachConfig["newUserFormGroup"].controls["username"].setErrors({notVerified: true});
          eachConfig["newUserFormGroup"].controls["username"].markAsTouched();
          eachConfig["newUserFormErrorMessages"] = {
            ...eachConfig["newUserFormErrorMessages"],
            username: "Username not verified"
          };
        }
      } else {
        eachConfig["existingUserFormErrorMessages"] = {
          ...(this.commonService.checkFormValidation(eachConfig["existingUserFormGroup"], this.existingUserFormErrorMessageMap))
        };
      }

      if (Object.keys(eachConfig["newUserFormErrorMessages"]).length ||
          Object.keys(eachConfig["existingUserFormErrorMessages"]).length) {
        hasError = true;
      }

      if (!this.checkObjectValueIsEmpty(Object.values(eachConfig["newUserFormGroup"].getRawValue()))) {
        userDetails.push({
          ...eachConfig["newUserFormGroup"].getRawValue(),
          uuid: eachConfig.uuid
        });
      }
      if (!this.checkObjectValueIsEmpty(Object.values(eachConfig["existingUserFormGroup"].getRawValue()))) {
        userDetails.push({
          userId: eachConfig["existingUserFormGroup"]?.controls?.userId?.getRawValue(),
          isLar: eachConfig["existingUserFormGroup"]?.controls?.isLar?.getRawValue()
        });
      }

    });

    if (Number(this.form.controls.role.value) >= roles.adult) {
      this.participantConfiguration.forEach(eachConfig => {
        if (eachConfig["isNewUser"]) {
          eachConfig["newUserFormErrorMessages"] = {
            ...(this.commonService.checkFormValidation(eachConfig["newUserFormGroup"], this.newUserFormErrorMessageMap))
          };
          if (eachConfig["newUserFormGroup"].controls["username"].value &&
              !eachConfig.isUserVerified && !eachConfig["newUserFormGroup"].controls["username"].invalid) {
            eachConfig["newUserFormGroup"].controls["username"].setErrors({notVerified: true});
            eachConfig["newUserFormGroup"].controls["username"].markAsTouched();
            eachConfig["newUserFormErrorMessages"] = {
              ...eachConfig["newUserFormErrorMessages"],
              username: "Username not verified"
            };
          }
        } else {
          eachConfig["existingUserFormErrorMessages"] = {
            ...(this.commonService.checkFormValidation(eachConfig["existingUserFormGroup"], this.existingUserFormErrorMessageMap))
          };
        }

        if (Object.keys(eachConfig["newUserFormErrorMessages"]).length ||
            Object.keys(eachConfig["existingUserFormErrorMessages"]).length) {
          hasError = true;
        }

        if (!this.checkObjectValueIsEmpty(Object.values(eachConfig["newUserFormGroup"].getRawValue()))) {
          participant = {
            ...eachConfig["newUserFormGroup"].getRawValue(),
            uuid: eachConfig.uuid
          };
        }
        if (!this.checkObjectValueIsEmpty(Object.values(eachConfig["existingUserFormGroup"].getRawValue()))) {
          participant = {
            userId: eachConfig["existingUserFormGroup"]?.controls?.userId?.getRawValue()
          };
        }
      });
    }
    if (hasError) {
      $(".form-field.ng-invalid")[0].parentElement.scrollIntoView();
      return;
    }

    const payload = {

      ...(!this.checkObjectValueIsEmpty(participant) ? {participant} : {}),

      [this.payloadRoleKeyLabelMap[this.selectedRole]]: userDetails,
      role: this.selectedRole,
      ...(this.userRole === roles["superAdmin"] && this.selectedRole > roles["superAdmin"] ? {clientId: this.clientId} : {}),
      ...(this.groupId ? {groupId: this.groupId} : {})
    };

    if (type === "submit") {
      this.store.dispatch(InviteUserRequest({payload}));
    } else if (type === "preview") {
      this.store.dispatch(FetchEmailPreviewDataRequest({payload}));
    }
  }

  deleteUser(i) {
    if (this.emailPreviewData) {
      // skip when email preview
      return;
    }
    this.store.dispatch(ReleaseUsernameRequest({
      payload: {
        uuid: this.configuration[i].uuid
      }
    }));
    this.configuration.splice(i, 1);
  }

  validateDateOfBirth(control: AbstractControl): ValidationErrors | null {
    const dob = control.value;
    if (dob < this.minDobDate) {
      return {invalidParticipantPastDate: true};
    }
    if (dob > this.maxDobDate) {
      return {invalidParticipantFutureDate: true};
    }
    return null;
  }

  checkObjectValueIsEmpty(obj) {
    for (const key in obj) {
      if (obj[key] !== null && obj[key] != "") {
        return false;
      }
    }
    return true;
  }

  onLarChanged(index: number) {

    for (let i = 0; i < this.configuration.length; i++) {
      if (i !== index) {
        this.configuration[i].newUserFormGroup.patchValue({
          isLar: false,
        }, {emitEvent: false});
        this.configuration[i].existingUserFormGroup.patchValue({
          isLar: false,
        }, {emitEvent: false});
      }
    }
  }

  setUsernameVerificationStatus(data: any) {
    this.participantConfiguration.forEach((item) => {
      if (item.uuid === data?.uuid) {
        item.isUserVerified = data?.isUsernameVerified;
        if (item.isUserVerified) {
          item["newUserFormGroup"].controls["username"].setErrors(null);
          item["newUserFormErrorMessages"] = {
            ...item["newUserFormErrorMessages"],
            username: undefined
          };
        }
      }
    });
    this.configuration.forEach((item) => {
      if (item.uuid === data?.uuid) {
        item.isUserVerified = data?.isUsernameVerified;
        if (item.isUserVerified) {
          item["newUserFormGroup"].controls["username"].setErrors(null);
          item["newUserFormErrorMessages"] = {
            ...item["newUserFormErrorMessages"],
            username: undefined
          };
        }
      }
    });
  }

  groupNextPage() {
    if (this.groupOptions.hasNext && !this.groupOptions.loading) {
      this.fetchListGroups(this.groupOptions.pageNumber + 1);
    }
  }

  fetchListGroups(pageNumber: number, searchText?: string) {
    this.store.dispatch(FetchGroupsFilterRequest({
      payload: {
        clientId: this.form.value.client,
        status: [
          1, 2
        ],
        pageNumber: pageNumber,
        search: searchText,
        shouldInclude: this.form.value.group,
        sortColumn: 'startDate',
        sortOrder: sortOrderMap.desc
      }
    }));
  }

  preview(manualEmail = false) {
    this.commonService.openDialog({
      data: {
        component: EmailPreviewComponent,
        data: {
          manualEmail,
          emailPreviewData: this.emailPreviewData
        }
      }
    })
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Check if date is valid
    if (isNaN(date.getTime())) return '';
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  clearFrom() {
    this.emailPreviewData = null;
    this.inviteService.resetForm.next();
    this.userCreated = false
    this.store.dispatch(SetEmailPreviewData({emailPreviewData: []}));
  }

  ngOnDestroy() {
    this.clearFrom();
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
